import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Megaphone, Sparkles, X } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Modal-style composer for creating a course-scoped announcement.
 * Includes the AI Beautify button (POST /ai/beautify-announcement) so
 * instructors can polish drafts before publishing.
 *
 * Visibility on the parent: instructors only — gate the trigger button
 * upstream via canEdit.
 */
export function AnnouncementComposer({
  token,
  courseId,
  onClose,
}: {
  token: string;
  courseId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sendEmail, setSendEmail] = useState(false);

  const createMutation = useMutation({
    mutationFn: () =>
      api.announcements.create(token, {
        scope: 'course',
        title,
        bodyHtml: body,
        courseId,
        sendEmail,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['announcements', courseId] });
      onClose();
    },
  });

  const [beautifyNotice, setBeautifyNotice] = useState<string | null>(null);
  const beautifyMutation = useMutation({
    mutationFn: () => api.ai.beautifyAnnouncement(token, { bodyHtml: body }),
    onSuccess: ({ bodyHtml, simulated }) => {
      // In simulate mode (AIML_API_KEY unset), the API returns a placeholder
      // string ("[simulated AI reply...]") instead of polished prose. Replacing
      // the user's draft with that would be destructive — leave their text
      // alone and surface a notice instead.
      if (simulated) {
        setBeautifyNotice('AI Beautify is offline (key not set) — your text is unchanged.');
        window.setTimeout(() => setBeautifyNotice(null), 4000);
        return;
      }
      setBody(bodyHtml);
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4 backdrop-blur-sm">
      <form
        className="flex w-full max-w-xl flex-col rounded-2xl border border-border bg-background shadow-2xl"
        onSubmit={(e) => {
          e.preventDefault();
          if (!title.trim() || !body.trim() || createMutation.isPending) return;
          void createMutation.mutateAsync();
        }}
      >
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-accent" aria-hidden />
            <h2 className="font-display text-lg font-semibold">New course announcement</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-3 p-5">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Announcement title"
            autoFocus
            className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />

          <div className="rounded-lg border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Body (HTML allowed)
              </span>
              <button
                type="button"
                onClick={() => {
                  if (!body.trim() || beautifyMutation.isPending) return;
                  void beautifyMutation.mutateAsync();
                }}
                disabled={!body.trim() || beautifyMutation.isPending}
                className={cn(
                  'inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium transition-colors',
                  beautifyMutation.isPending
                    ? 'opacity-60'
                    : 'text-accent hover:bg-accent/10 disabled:opacity-40',
                )}
              >
                <Sparkles className="h-3 w-3" />
                {beautifyMutation.isPending ? 'Polishing…' : 'AI Beautify'}
              </button>
            </div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              placeholder="What do you want students to know? You can paste HTML."
              className="w-full resize-none rounded-b-lg bg-card px-3 py-2 text-sm focus:outline-none"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="h-4 w-4 rounded border-border"
            />
            Also email recipients
          </label>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border bg-card/50 px-5 py-3">
          {beautifyNotice && (
            <span className="mr-auto text-xs text-muted-foreground">{beautifyNotice}</span>
          )}
          {createMutation.isError && (
            <span className="mr-auto text-xs text-destructive">Couldn't publish — try again.</span>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!title.trim() || !body.trim() || createMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {createMutation.isPending ? 'Publishing…' : 'Publish'}
          </button>
        </footer>
      </form>
    </div>
  );
}

/**
 * Read-only list of recent announcements for a course. Rendered in
 * CourseDetail's header (collapsible) so the latest announcement is
 * always one click away.
 */
export function CourseAnnouncementsStrip({ token, courseId }: { token: string; courseId: string }) {
  const { data } = useQuery({
    queryKey: ['announcements', courseId],
    queryFn: () => api.announcements.listForCourse(token, courseId),
    staleTime: 60_000,
    enabled: token.length > 0,
  });
  const items = data?.announcements ?? [];
  if (items.length === 0) return null;
  const latest = items[0];
  if (!latest) return null;

  return (
    <div className="rounded-2xl border border-accent/30 bg-accent/5 p-3 text-sm">
      <div className="flex items-center gap-2 text-accent">
        <Megaphone className="h-3.5 w-3.5" />
        <span className="text-[11px] uppercase tracking-wider">Latest announcement</span>
      </div>
      <div className="mt-1 font-semibold">{latest.title}</div>
      <div
        className="mt-1 line-clamp-2 text-xs text-muted-foreground"
        dangerouslySetInnerHTML={{ __html: latest.bodyHtml }}
      />
    </div>
  );
}
